import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Home, RefreshCw, Search, Lightbulb, Loader2 } from "lucide-react";
import * as Sentry from '@sentry/react';
import { logger } from '@/lib/logger';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  isChunkError: boolean;
  autoReloadCountdown: number;
}

/**
 * Checks if an error is a chunk loading error
 */
function isChunkLoadError(error: Error): boolean {
  const chunkErrorPatterns = [
    'Failed to fetch dynamically imported module',
    'Loading chunk',
    'ChunkLoadError',
    'Loading CSS chunk',
    'Unable to preload CSS',
  ];
  
  return chunkErrorPatterns.some(pattern => 
    error.message.includes(pattern) || error.name.includes(pattern)
  );
}

class ErrorBoundary extends Component<Props, State> {
  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  public state: State = {
    hasError: false,
    isChunkError: false,
    autoReloadCountdown: 3,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    const isChunk = isChunkLoadError(error);
    return { 
      hasError: true, 
      error,
      isChunkError: isChunk,
      autoReloadCountdown: isChunk ? 3 : 0,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isChunk = isChunkLoadError(error);
    
    logger.error("[ErrorBoundary] Erro capturado:", error, errorInfo);
    
    // Reporta erro para Sentry com contexto completo
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        error_boundary: 'global',
        is_chunk_error: isChunk,
      },
    });

    // Auto-reload for chunk errors
    if (isChunk) {
      this.startAutoReloadCountdown();
    }
  }

  private startAutoReloadCountdown = () => {
    // Check if we already tried reloading recently
    const reloadKey = 'error_boundary_reload';
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();
    
    if (lastReload && (now - parseInt(lastReload, 10)) < 30000) {
      // Already reloaded in last 30 seconds, don't auto-reload
      logger.warn('[ErrorBoundary] Already reloaded recently, showing manual option');
      this.setState({ autoReloadCountdown: 0 });
      return;
    }

    this.countdownInterval = setInterval(() => {
      this.setState(prev => {
        const newCount = prev.autoReloadCountdown - 1;
        if (newCount <= 0) {
          if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
          }
          sessionStorage.setItem(reloadKey, now.toString());
          window.location.reload();
        }
        return { autoReloadCountdown: newCount };
      });
    }, 1000);
  };

  public componentWillUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, isChunkError: false, autoReloadCountdown: 0 });
    window.location.href = "/";
  };

  public render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;
      const { isChunkError, autoReloadCountdown } = this.state;

      // Special UI for chunk errors with auto-reload
      if (isChunkError && autoReloadCountdown > 0) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-background p-4">
            <div className="max-w-md w-full space-y-6 text-center bg-card p-8 rounded-3xl border-2 border-border/50 shadow-lg">
              <div className="flex justify-center">
                <div className="rounded-full bg-primary/10 p-4">
                  <Loader2 className="h-10 w-10 text-primary animate-spin" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h1 className="text-xl font-heading font-bold text-foreground">
                  atualizando a experiência...
                </h1>
                <p className="text-muted-foreground font-lexend leading-relaxed">
                  encontramos uma versão mais recente. recarregando em {autoReloadCountdown}s
                </p>
              </div>

              <Button 
                onClick={() => window.location.reload()} 
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                recarregar agora
              </Button>
            </div>
          </div>
        );
      }
      
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="max-w-2xl w-full space-y-6 text-center bg-card p-8 rounded-3xl border-2 border-border/50 shadow-lg">
            <div className="flex justify-center">
              <div className="rounded-full bg-soul-warmth/10 p-4">
                <AlertTriangle className="h-12 w-12 text-soul-alert" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-heading font-bold text-foreground">
                {isChunkError 
                  ? 'atualização disponível' 
                  : 'tivemos um tropeço na jornada'}
              </h1>
              <p className="text-muted-foreground font-lexend leading-relaxed">
                {isChunkError
                  ? 'uma nova versão está disponível. clique em recarregar para continuar.'
                  : isDev 
                    ? 'erro capturado pelo sistema. veja os detalhes técnicos abaixo.'
                    : 'algo inesperado aconteceu, mas não se preocupe. estamos aqui para te ajudar a seguir em frente.'}
              </p>
            </div>

            {/* Stack trace: sempre visível em DEV, colapsado em PROD */}
            {this.state.error && !isChunkError && (
              <details className="text-left" open={isDev}>
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors font-medium flex items-center gap-1.5">
                  {isDev && <Search className="h-3 w-3" />}
                  {isDev ? 'stack trace completo' : 'detalhes técnicos'}
                </summary>
                <div className="mt-3 space-y-3">
                  <div className="p-3 bg-destructive/10 border-l-4 border-destructive rounded-r">
                    <div className="text-xs font-medium text-destructive mb-1">erro:</div>
                    <div className="text-sm text-foreground font-mono">{this.state.error.message}</div>
                  </div>
                  
                  {this.state.error.stack && (
                    <div className="p-3 bg-muted border border-border rounded-lg">
                      <div className="text-xs font-medium text-muted-foreground mb-2">stack trace:</div>
                      <pre className="text-[10px] font-mono overflow-auto max-h-96 text-muted-foreground leading-relaxed whitespace-pre-wrap">
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                  
                  {isDev && (
                    <div className="text-[10px] text-muted-foreground italic p-2 bg-accent/5 rounded flex items-center gap-1.5">
                      <Lightbulb className="h-3 w-3" />
                      este erro foi reportado ao sentry com contexto completo
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center pt-2">
              {isChunkError ? (
                <Button 
                  onClick={() => window.location.reload()} 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  recarregar página
                </Button>
              ) : (
                <>
                  <Button 
                    onClick={this.handleReset} 
                    className="bg-soul-focus hover:bg-soul-achievement text-white"
                  >
                    <Home className="mr-2 h-4 w-4" />
                    voltar ao início
                  </Button>
                  <Button 
                    onClick={() => window.location.reload()} 
                    variant="outline"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    tentar novamente
                  </Button>
                </>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground pt-2">
              se o problema persistir, estamos aqui para caminhar junto com você
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
